import { useLocation, NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Fragment } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import { Box, Columns, Column, Text, Stack } from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'
import { GraphBarAlt, GridAlt, RocketAlt, FileLockAlt, EditAlt } from '@/components/icons'
import Skeleton from '@/components/skeleton'
import useProfile from '@/hooks/use-profile'
import useMediaQuery from '@/hooks/use-media-query'
import { breakpoints, structure } from '@/styles'
import useGetPlanKey from '@/hooks/use-get-plan-key'
import { toUnit } from '@/utils/styles'
import * as styles from '../../index.css'

const activeClassName = 'nav-item-active'
const MenuItem = styled(NavLink)`
  color: ${vars.foregroundColor.navItem};
  :hover {
    color: ${vars.foregroundColor.navItemActive};
  }

  &.${activeClassName} {
    color: ${vars.foregroundColor.navItemActive};
  }
`

const LoadingMenu = () => (
  <Box padding="xxxxsmall">
    <Skeleton height={16} width={200} />
  </Box>
)

const SideMenu = () => {
  const displayDesktopSmall = useMediaQuery(`(max-width: ${breakpoints.desktopSmall})`)
  const { t } = useTranslation()
  const planText = useGetPlanKey()

  const SUBPRODUCT_OPTIONS = [
    {
      title: t('l.credit_usage.sidemenu.tenant_screening.label', 'Tenant Screening'),
      description: t(
        'l.credit_usage.sidemenu.tenant_screening.text',
        'View tenant screening reports.'
      ),
      icon: FileLockAlt,
      to: '/usage-credits/products/tenant-screening',
      key: ['tenant-screening'],
      id: `usage-credit-${planText}-tenant-screening-tab`
    },
    {
      title: t('l.credit_usage.sidemenu.rental_advertising.label', 'Rental Advertising'),
      description: t(
        'l.credit_usage.sidemenu.rental_advertising.text',
        'View rental advertising products.'
      ),
      icon: RocketAlt,
      to: '/usage-credits/products/rental-advertising',
      key: ['rental-advertising'],
      id: `usage-credit-${planText}-rental-advert-tab`
    },
    {
      title: t('l.credit_usage.sidemenu.lease_payments.label', 'Lease & Payments'),
      description: t(
        'l.credit_usage.sidemenu.lease_payments.text',
        'View lease & payment products.'
      ),
      icon: EditAlt,
      to: '/usage-credits/products/lease-payments',
      key: ['lease-payments'],
      id: `usage-credit-${planText}-lease-payment-tab`
    }
  ]

  const PRODUCT_OPTIONS = [
    {
      title: t('b.useage.credits.my_products.text', 'My Products'),
      description: t(
        'l.credit_usage.sidemenu.my_products.text',
        'View your products and their usage.'
      ),
      icon: GraphBarAlt,
      to: '/usage-credits/products/my-products',
      key: ['my-products'],
      id: `usage-credit-${planText}-my-products-tab`
    },
    {
      title: t('l.credit_usage.sidemenu.all_products.label', 'All Products'),
      description: t('l.credit_usage.sidemenu.all_products.text', 'View all products.'),
      icon: GridAlt,
      to: '/usage-credits/products/all',
      key: ['all'],
      submenuItems: SUBPRODUCT_OPTIONS,
      id: `usage-credit-${planText}-all-products-tab`
    }
  ]

  return (
    <Box
      component="menu"
      background="neutralLight"
      style={{
        height: `calc(100vh - ${toUnit(structure.header.height)})`,
        borderRight: `1px solid ${vars.borderColor.neutralLight}`
      }}
      paddingY={displayDesktopSmall ? 'large' : 'xlarge'}
      padding={{ mobile: 'gutter', desktop: 'gutter', wide: 'xlarge' }}
    >
      <Stack space="none" align="left">
        <OptionList
          title={t('l.credit_usage.sidemenu.filter_by.text', 'Filter by:')}
          data={PRODUCT_OPTIONS}
        />
      </Stack>
    </Box>
  )
}

const OptionList = ({ title, data }: { title?: string; data: any[] }) => {
  const location = useLocation()
  const pathName = location.pathname
  const profile = useProfile()
  const isLoading = !profile?.company_info

  return (
    <Stack space="small" align="left">
      {title ? (
        isLoading ? (
          <Skeleton height={16} width={200} />
        ) : (
          <Text tone="secondary" size="small">
            {title}
          </Text>
        )
      ) : null}
      <Stack space="xxsmall">
        {data.map(item => {
          const Icon = item.icon
          const isActive = item.key.some((itemKey: any) => pathName.includes(itemKey))
          return isLoading ? (
            <LoadingMenu key={item.key[0]} />
          ) : (
            <Fragment key={item.key[0]}>
              <Box
                component="li"
                borderRadius="small"
                key={item.key}
                className={`${styles.menuItem} ${clsx({
                  [styles.menuItemActive]: isActive
                })}`}
              >
                <MenuItem activeClassName={activeClassName} to={item.to} id={item?.id}>
                  <Box padding="xxsmall">
                    <Columns space="xxxsmall" alignY="center">
                      <Column width="content">
                        <Box display="flex" className={styles.iconWrapper}>
                          <Icon width={20} />
                        </Box>
                      </Column>
                      <Column>
                        <Text tone="secondary" size="small" weight="medium" baseline={false}>
                          {item.title}
                        </Text>
                      </Column>
                    </Columns>
                  </Box>
                </MenuItem>
              </Box>
              {item?.submenuItems?.map((item: any) => {
                const Icon = item.icon
                const isActive = item.key.some((itemKey: any) => pathName.includes(itemKey))

                return (
                  <Box paddingLeft="medium" key={item.key[0]}>
                    <Box
                      component="li"
                      borderRadius="small"
                      key={item.key}
                      className={`${styles.menuItem} ${clsx({
                        [styles.menuItemActive]: isActive
                      })}`}
                    >
                      <MenuItem activeClassName={activeClassName} to={item.to} id={item?.id}>
                        <Box padding="xxsmall">
                          <Columns space="xxxsmall" alignY="center">
                            <Column width="content">
                              <Box display="flex" className={styles.iconWrapper}>
                                <Icon width={20} />
                              </Box>
                            </Column>
                            <Column>
                              <Text tone="secondary" size="small" weight="medium" baseline={false}>
                                {item.title}
                              </Text>
                            </Column>
                          </Columns>
                        </Box>
                      </MenuItem>
                    </Box>
                  </Box>
                )
              })}
            </Fragment>
          )
        })}
      </Stack>
    </Stack>
  )
}

export default SideMenu
